import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { Briefcase, Plus, Edit, ToggleLeft, ToggleRight, Users, AlertCircle, Lock, HelpCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import styles from './DepartmentPositionsTab.module.css';
import modalStyles from './Modal.module.css';

interface Position {
  id: number;
  title: string;
  description?: string;
  department: string;
  tier: number;
  rank?: number;
  guide?: string;
  requirements?: string;
  commitmentLevel: number;
  isActive: boolean;
  canManageMedicalTickets: boolean;
  maxHolders?: number;
  createdAt: string;
  updatedAt?: string;
}

interface DepartmentPositionsTabProps {
  department: string;
}

const DepartmentPositionsTab: React.FC<DepartmentPositionsTabProps> = ({ department }) => {
  const { user } = useAuth();
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPositions();
  }, [department]);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/Positions/department/${department}`, {
        params: { includeInactive: true }
      });
      console.log('Positions response:', response.data);
      setPositions(response.data);
    } catch (error) {
      console.error('Failed to fetch positions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (position: Position) => {
    try {
      setProcessing(true);
      await axios.put(`/Positions/${position.id}`, {
        ...position,
        isActive: !position.isActive
      });
      fetchPositions();
    } catch (error) {
      console.error('Failed to toggle position:', error);
      alert('Failed to toggle position status. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const getTierLabel = (tier: number): string => {
    switch (tier) {
      case 4: return 'C-Suite';
      case 3: return 'Senior';
      case 2: return 'Mid-Level';
      case 1: return 'Entry';
      default: return 'Unknown';
    }
  };

  const getTierColor = (tier: number): string => {
    switch (tier) {
      case 4: return '#fbbf24'; // Gold
      case 3: return '#3b82f6'; // Blue
      case 2: return '#10b981'; // Green
      case 1: return '#6b7280'; // Gray
      default: return '#6b7280';
    }
  };

  const getCommitmentLabel = (level: number): string => {
    switch (level) {
      case 3: return 'Dedicated';
      case 2: return 'Regular';
      case 1: return 'Casual';
      default: return 'Unknown';
    }
  };

  const getCommitmentColor = (level: number): string => {
    const colors: { [key: number]: string } = {
      0: '#6b7280',
      1: '#6b7280',
      2: '#3b82f6',
      3: '#8b5cf6',
      4: '#dc2626'
    };
    return colors[level] || '#6b7280';
  };

  // Check if user can manage C-Suite (Tier 4) positions
  const canManageTier4 = (): boolean => {
    if (!user) return false;
    
    // Admins can manage all positions
    if (user.role === 'Admin') return true;
    
    // Check if user is CEO
    const titles = [
      user.primaryPositionTitle,
      user.secondaryPositionTitle,
      user.tertiaryPositionTitle
    ].filter(Boolean).map((t: string) => t.toLowerCase());
    
    return titles.includes('ceo') || titles.includes('chief executive officer');
  };

  // Check if position can be edited/toggled by current user
  const canManagePosition = (position: Position): boolean => {
    if (position.tier === 4) {
      return canManageTier4();
    }
    return true; // Non-C-Suite positions can be managed by department leaders
  };

  const activePositions = positions.filter(p => p.isActive);
  const inactivePositions = positions.filter(p => !p.isActive);

  if (loading) {
    return <div className={styles.loading}>Loading positions...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <Briefcase className={styles.statIcon} />
            <div className={styles.statContent}>
              <p className={styles.statValue}>{activePositions.length}</p>
              <p className={styles.statLabel}>Active Positions</p>
            </div>
          </div>
          
          <div className={styles.statCard}>
            <Users className={styles.statIcon} />
            <div className={styles.statContent}>
              <p className={styles.statValue}>{inactivePositions.length}</p>
              <p className={styles.statLabel}>Inactive Positions</p>
            </div>
          </div>
        </div>

        <button className={styles.createBtn} onClick={() => setShowCreateModal(true)}>
          <Plus size={20} />
          Create Position
        </button>
      </div>

      {positions.length === 0 ? (
        <div className={styles.empty}>
          <AlertCircle size={48} />
          <p>No positions in this department</p>
          <button className={styles.createBtnLarge} onClick={() => setShowCreateModal(true)}>
            <Plus size={20} />
            Create First Position
          </button>
        </div>
      ) : (
        <>
          {/* Active Positions */}
          {activePositions.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Active Positions</h3>
              <div className={styles.positionsGrid}>
                {activePositions.map((position) => (
                  <div key={position.id} className={styles.positionCard}>
                    <div className={styles.cardHeader}>
                      <div className={styles.titleRow}>
                        <h4 className={styles.positionTitle}>
                          {position.title}
                          {position.tier === 4 && !canManagePosition(position) && (
                            <span title="Restricted to Admin and CEO">
                              <Lock size={16} className={styles.lockIcon} />
                            </span>
                          )}
                        </h4>
                        <span 
                          className={styles.tierBadge}
                          style={{ backgroundColor: getTierColor(position.tier) }}
                        >
                          {getTierLabel(position.tier)}
                        </span>
                      </div>
                      
                      <div className={styles.commitmentBadge} style={{ color: getCommitmentColor(position.commitmentLevel) }}>
                        {getCommitmentLabel(position.commitmentLevel)}
                      </div>
                    </div>

                    {position.description && (
                      <p className={styles.description}>{position.description}</p>
                    )}

                    <div className={styles.metadata}>
                      {position.maxHolders && (
                        <div className={styles.metaItem}>
                          <Users size={16} />
                          <span>Max: {position.maxHolders}</span>
                        </div>
                      )}
                      {position.canManageMedicalTickets && (
                        <div className={styles.metaItem}>
                          <span className={styles.medicalBadge}>Medical Access</span>
                        </div>
                      )}
                    </div>

                    <div className={styles.cardActions}>
                      <button
                        className={styles.editBtn}
                        onClick={() => setSelectedPosition(position)}
                        disabled={!canManagePosition(position)}
                        title={!canManagePosition(position) ? 'Only Admin and CEO can edit C-Suite positions' : ''}
                      >
                        {!canManagePosition(position) && <Lock size={14} />}
                        <Edit size={16} />
                        Edit
                      </button>
                      <button
                        className={styles.toggleBtn}
                        onClick={() => handleToggleActive(position)}
                        disabled={processing || !canManagePosition(position)}
                        title={!canManagePosition(position) ? 'Only Admin and CEO can modify C-Suite positions' : ''}
                      >
                        <ToggleRight size={16} />
                        Deactivate
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inactive Positions */}
          {inactivePositions.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Inactive Positions</h3>
              <div className={styles.positionsGrid}>
                {inactivePositions.map((position) => (
                  <div key={position.id} className={`${styles.positionCard} ${styles.inactive}`}>
                    <div className={styles.cardHeader}>
                      <div className={styles.titleRow}>
                        <h4 className={styles.positionTitle}>
                          {position.title}
                          {position.tier === 4 && !canManagePosition(position) && (
                            <span title="Restricted to Admin and CEO">
                              <Lock size={16} className={styles.lockIcon} />
                            </span>
                          )}
                        </h4>
                        <span 
                          className={styles.tierBadge}
                          style={{ backgroundColor: getTierColor(position.tier) }}
                        >
                          {getTierLabel(position.tier)}
                        </span>
                      </div>
                    </div>

                    {position.description && (
                      <p className={styles.description}>{position.description}</p>
                    )}

                    <div className={styles.cardActions}>
                      <button
                        className={styles.editBtn}
                        onClick={() => setSelectedPosition(position)}
                        disabled={!canManagePosition(position)}
                        title={!canManagePosition(position) ? 'Only Admin and CEO can edit C-Suite positions' : ''}
                      >
                        {!canManagePosition(position) && <Lock size={14} />}
                        <Edit size={16} />
                        Edit
                      </button>
                      <button
                        className={styles.toggleBtn}
                        onClick={() => handleToggleActive(position)}
                        disabled={processing || !canManagePosition(position)}
                        title={!canManagePosition(position) ? 'Only Admin and CEO can modify C-Suite positions' : ''}
                      >
                        <ToggleLeft size={16} />
                        Activate
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className={modalStyles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div className={`${modalStyles.modalContent} ${modalStyles.modalMedium}`} onClick={(e) => e.stopPropagation()}>
            <div className={modalStyles.modalHeader}>
              <h2>Create New Position</h2>
            </div>
            <form id="createPositionForm" onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              
              try {
                setProcessing(true);
                await axios.post('/Positions', {
                  title: formData.get('title'),
                  description: formData.get('description'),
                  department: department,
                  tier: parseInt(formData.get('tier') as string),
                  commitmentLevel: parseInt(formData.get('commitmentLevel') as string),
                  requirements: formData.get('requirements'),
                  guide: formData.get('guide'),
                  maxHolders: formData.get('maxHolders') ? parseInt(formData.get('maxHolders') as string) : null,
                  canManageMedicalTickets: department.toLowerCase() === 'medical' && formData.get('canManageMedicalTickets') === 'on'
                });
                
                setShowCreateModal(false);
                fetchPositions();
              } catch (error: any) {
                console.error('Failed to create position:', error);
                alert(error.response?.data?.message || 'Failed to create position. Please try again.');
              } finally {
                setProcessing(false);
              }
            }}>
              <div className={modalStyles.modalBody}>
                <div className={styles.formFields}>
              <div className={styles.formGroup}>
                <label htmlFor="title">
                  <span className={styles.tooltip}>
                    Position Title *
                    <HelpCircle className={styles.tooltipIcon} />
                    <span className={styles.tooltipText}>A clear, descriptive name for this position</span>
                  </span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  maxLength={100}
                  placeholder="e.g., Field Medic, Convoy Commander"
                  className={modalStyles.modalInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="description">
                  <span className={styles.tooltip}>
                    Description
                    <HelpCircle className={styles.tooltipIcon} />
                    <span className={styles.tooltipText}>Brief overview of the role and responsibilities</span>
                  </span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  maxLength={500}
                  rows={3}
                  placeholder="Brief description of this position's role and responsibilities"
                  className={modalStyles.modalTextarea}
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="tier">
                    <span className={styles.tooltip}>
                      Tier *
                      <HelpCircle className={styles.tooltipIcon} />
                      <span className={styles.tooltipText}>Position level: Entry/Mid/Senior/C-Suite</span>
                    </span>
                  </label>
                  <select id="tier" name="tier" required className={modalStyles.modalSelect} defaultValue="1">
                    <option value="1">Entry Level</option>
                    <option value="2">Mid-Level</option>
                    <option value="3">Senior</option>
                    {canManageTier4() && <option value="4">C-Suite</option>}
                  </select>
                  <small className={styles.helpText}>Only Admin and CEO can create C-Suite positions</small>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="commitmentLevel">
                    <span className={styles.tooltip}>
                      Commitment *
                      <HelpCircle className={styles.tooltipIcon} />
                      <span className={styles.tooltipText}>Expected time commitment level for this role</span>
                    </span>
                  </label>
                  <select id="commitmentLevel" name="commitmentLevel" required className={modalStyles.modalSelect} defaultValue="1">
                    <option value="1">Casual</option>
                    <option value="2">Regular</option>
                    <option value="3">Dedicated</option>
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="maxHolders">
                    <span className={styles.tooltip}>
                      Max Holders
                      <HelpCircle className={styles.tooltipIcon} />
                      <span className={styles.tooltipText}>Maximum number of people who can hold this position</span>
                    </span>
                  </label>
                  <input
                    type="number"
                    id="maxHolders"
                    name="maxHolders"
                    min="1"
                    max="100"
                    placeholder="Optional"
                    className={modalStyles.modalInput}
                  />
                </div>

              {department.toLowerCase() === 'medical' && (
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="canManageMedicalTickets"
                      className={styles.checkbox}
                    />
                    <span>Can Manage Medical Tickets</span>
                  </label>
                </div>
              )}

              <div className={styles.formGroup}>
                <label htmlFor="requirements">
                  <span className={styles.tooltip}>
                    Requirements
                    <HelpCircle className={styles.tooltipIcon} />
                    <span className={styles.tooltipText}>Qualifications, skills, or prerequisites needed</span>
                  </span>
                </label>
                <textarea
                  id="requirements"
                  name="requirements"
                  rows={3}
                  placeholder="Required qualifications, prerequisites, or certifications..."
                  className={modalStyles.modalTextarea}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="guide">
                  <span className={styles.tooltip}>
                    Position Guide
                    <HelpCircle className={styles.tooltipIcon} />
                    <span className={styles.tooltipText}>Detailed responsibilities and best practices</span>
                  </span>
                </label>
                <textarea
                  id="guide"
                  name="guide"
                  rows={5}
                  placeholder="Detailed guide on responsibilities, expectations, and best practices..."
                  className={modalStyles.modalTextarea}
                />
              </div>
                </div>
              </div>
              <div className={modalStyles.modalFooter}>
                <button
                  type="button"
                  className={`${modalStyles.modalButton} ${modalStyles.modalButtonSecondary}`}
                  onClick={() => setShowCreateModal(false)}
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${modalStyles.modalButton} ${modalStyles.modalButtonPrimary}`}
                  disabled={processing}
                >
                  {processing ? 'Creating...' : 'Create Position'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {selectedPosition && (
        <div className={modalStyles.modalOverlay} onClick={() => setSelectedPosition(null)}>
          <div className={`${modalStyles.modalContent} ${modalStyles.modalMedium}`} onClick={(e) => e.stopPropagation()}>
            <div className={modalStyles.modalHeader}>
              <h2>Edit Position: {selectedPosition.title}</h2>
            </div>
            <form id="editPositionForm" onSubmit={async (e) => {
              e.preventDefault();
              setProcessing(true);
              
              const formData = new FormData(e.currentTarget);
              const updatedPosition = {
                title: formData.get('title') as string,
                description: formData.get('description') as string || undefined,
                department,
                tier: parseInt(formData.get('tier') as string),
                commitmentLevel: parseInt(formData.get('commitmentLevel') as string),
                maxHolders: formData.get('maxHolders') ? parseInt(formData.get('maxHolders') as string) : undefined,
                canManageMedicalTickets: department.toLowerCase() === 'medical' ? formData.get('canManageMedicalTickets') === 'on' : false,
                requirements: formData.get('requirements') as string || undefined,
                guide: formData.get('guide') as string || undefined,
                isActive: formData.get('isActive') === 'on'
              };

              try {
                await axios.put(`/Positions/${selectedPosition.id}`, updatedPosition);
                setSelectedPosition(null);
                fetchPositions();
              } catch (error: any) {
                console.error('Failed to update position:', error);
                alert(error.response?.data?.message || 'Failed to update position. Please try again.');
              } finally {
                setProcessing(false);
              }
            }}>
              <div className={modalStyles.modalBody}>
                <div className={styles.formFields}>
              <div className={styles.formGroup}>
                <label htmlFor="edit-title">
                  <span className={styles.tooltip}>
                    Title *
                    <HelpCircle className={styles.tooltipIcon} />
                    <span className={styles.tooltipText}>A clear, descriptive name for this position</span>
                  </span>
                </label>
                <input
                  type="text"
                  id="edit-title"
                  name="title"
                  defaultValue={selectedPosition.title}
                  maxLength={100}
                  required
                  className={modalStyles.modalInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="edit-description">
                  <span className={styles.tooltip}>
                    Description
                    <HelpCircle className={styles.tooltipIcon} />
                    <span className={styles.tooltipText}>Brief overview of the role and responsibilities</span>
                  </span>
                </label>
                <textarea
                  id="edit-description"
                  name="description"
                  defaultValue={selectedPosition.description || ''}
                  maxLength={500}
                  rows={3}
                  className={modalStyles.modalTextarea}
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="edit-tier">
                    <span className={styles.tooltip}>
                      Tier *
                      <HelpCircle className={styles.tooltipIcon} />
                      <span className={styles.tooltipText}>Position level: Entry/Mid/Senior/C-Suite</span>
                    </span>
                  </label>
                  <select 
                    id="edit-tier" 
                    name="tier" 
                    defaultValue={selectedPosition.tier.toString()}
                    required 
                    className={modalStyles.modalSelect}
                    disabled={selectedPosition.tier === 4 && !canManageTier4()}
                  >
                    <option value="1">1 - Entry Level</option>
                    <option value="2">2 - Mid-Level</option>
                    <option value="3">3 - Senior</option>
                    {canManageTier4() && <option value="4">4 - C-Suite</option>}
                  </select>
                  {selectedPosition.tier === 4 && !canManageTier4() && (
                    <p className={styles.helpText}>Only Admin and CEO can modify C-Suite positions</p>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="edit-commitment">
                    <span className={styles.tooltip}>
                      Commitment Level *
                      <HelpCircle className={styles.tooltipIcon} />
                      <span className={styles.tooltipText}>Expected time commitment level for this role</span>
                    </span>
                  </label>
                  <select 
                    id="edit-commitment" 
                    name="commitmentLevel" 
                    defaultValue={selectedPosition.commitmentLevel.toString()}
                    required 
                    className={modalStyles.modalSelect}
                  >
                    <option value="1">Casual</option>
                    <option value="2">Regular</option>
                    <option value="3">Dedicated</option>
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="edit-maxHolders">
                    <span className={styles.tooltip}>
                      Max Holders
                      <HelpCircle className={styles.tooltipIcon} />
                      <span className={styles.tooltipText}>Maximum number of people who can hold this position</span>
                    </span>
                  </label>
                  <input
                    type="number"
                    id="edit-maxHolders"
                    name="maxHolders"
                    defaultValue={selectedPosition.maxHolders || ''}
                    min={1}
                    max={100}
                    className={modalStyles.modalInput}
                  />
                </div>

              {department.toLowerCase() === 'medical' && (
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="canManageMedicalTickets"
                      defaultChecked={selectedPosition.canManageMedicalTickets}
                      className={styles.checkbox}
                    />
                    <span>Can Manage Medical Tickets</span>
                  </label>
                </div>
              )}

              <div className={styles.formGroup}>
                <label htmlFor="edit-requirements">
                  <span className={styles.tooltip}>
                    Requirements
                    <HelpCircle className={styles.tooltipIcon} />
                    <span className={styles.tooltipText}>Qualifications, skills, or prerequisites needed</span>
                  </span>
                </label>
                <textarea
                  id="edit-requirements"
                  name="requirements"
                  defaultValue={selectedPosition.requirements || ''}
                  rows={3}
                  placeholder="Required qualifications, prerequisites, or certifications..."
                  className={modalStyles.modalTextarea}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="edit-guide">
                  <span className={styles.tooltip}>
                    Position Guide
                    <HelpCircle className={styles.tooltipIcon} />
                    <span className={styles.tooltipText}>Detailed responsibilities and best practices</span>
                  </span>
                </label>
                <textarea
                  id="edit-guide"
                  name="guide"
                  defaultValue={selectedPosition.guide || ''}
                  rows={5}
                  placeholder="Detailed guide on responsibilities, expectations, and best practices..."
                  className={modalStyles.modalTextarea}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isActive"
                    defaultChecked={selectedPosition.isActive}
                    className={styles.checkbox}
                  />
                  <span>Position is Active</span>
                </label>
              </div>
                </div>
              </div>
              <div className={modalStyles.modalFooter}>
                <button 
                  type="button" 
                  onClick={() => setSelectedPosition(null)} 
                  className={`${modalStyles.modalButton} ${modalStyles.modalButtonSecondary}`}
                  disabled={processing}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className={`${modalStyles.modalButton} ${modalStyles.modalButtonPrimary}`}
                  disabled={processing || (selectedPosition.tier === 4 && !canManageTier4())}
                >
                  {processing ? 'Updating...' : 'Update Position'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentPositionsTab;
